import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { useEffect, useState } from 'react';
import { auth, db, storage } from './firebase';

export type MediaKind = 'video' | 'image';

export type MediaRecord = {
  id: string;
  kind: MediaKind;
  title: string;
  label: string;
  src: string;
  poster: string;
  storagePath: string;
  posterPath: string;
  createdAt: number;
};

export type MediaInput = {
  kind: MediaKind;
  title: string;
  label: string;
};

const mediaRef = collection(db, 'media');

function fromDoc(id: string, data: Record<string, unknown>): MediaRecord {
  return {
    id,
    kind: data.kind === 'image' ? 'image' : 'video',
    title: typeof data.title === 'string' ? data.title : '',
    label: typeof data.label === 'string' ? data.label : '',
    src: typeof data.src === 'string' ? data.src : '',
    poster: typeof data.poster === 'string' ? data.poster : '',
    storagePath: typeof data.storagePath === 'string' ? data.storagePath : '',
    posterPath: typeof data.posterPath === 'string' ? data.posterPath : '',
    createdAt: typeof data.createdAt === 'number' ? data.createdAt : 0,
  };
}

export function useMedia() {
  const [items, setItems] = useState<MediaRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(mediaRef, orderBy('createdAt', 'desc'));
    return onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((item) => fromDoc(item.id, item.data())));
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, []);

  return { items, loading, error };
}

function safeName(file: File) {
  return file.name.replace(/[^a-zA-Z0-9._-]/g, '-').slice(0, 80);
}

export function uploadMediaFile(file: File, onProgress?: (pct: number) => void) {
  const uid = auth.currentUser?.uid;
  if (!uid) {
    return Promise.reject(new Error('Sign in to upload files.'));
  }

  const path = `media/${uid}/${Date.now()}-${safeName(file)}`;
  const task = uploadBytesResumable(ref(storage, path), file, { contentType: file.type });

  return new Promise<{ url: string; path: string }>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        if (snap.totalBytes) {
          onProgress?.(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
        }
      },
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve({ url, path });
      },
    );
  });
}

async function removeFile(path: string) {
  if (!path) return;
  try {
    await deleteObject(ref(storage, path));
  } catch {
    // File may already be gone; keep the console usable.
  }
}

export async function createMedia(
  input: MediaInput,
  file: File,
  posterFile: File | null,
  onProgress?: (pct: number) => void,
) {
  onProgress?.(0);
  const uploaded = await uploadMediaFile(file, onProgress);
  let poster = { url: '', path: '' };
  if (posterFile) {
    poster = await uploadMediaFile(posterFile, onProgress);
  }

  await addDoc(mediaRef, {
    kind: input.kind,
    title: input.title.trim(),
    label: input.label.trim(),
    src: uploaded.url,
    poster: poster.url,
    storagePath: uploaded.path,
    posterPath: poster.path,
    createdAt: Date.now(),
  });
}

export async function updateMedia(
  item: MediaRecord,
  input: MediaInput,
  file: File | null,
  posterFile: File | null,
  onProgress?: (pct: number) => void,
) {
  const next = {
    kind: input.kind,
    title: input.title.trim(),
    label: input.label.trim(),
    src: item.src,
    poster: item.poster,
    storagePath: item.storagePath,
    posterPath: item.posterPath,
  };

  if (file) {
    const uploaded = await uploadMediaFile(file, onProgress);
    await removeFile(item.storagePath);
    next.src = uploaded.url;
    next.storagePath = uploaded.path;
  }

  if (posterFile) {
    const uploaded = await uploadMediaFile(posterFile, onProgress);
    await removeFile(item.posterPath);
    next.poster = uploaded.url;
    next.posterPath = uploaded.path;
  }

  if (input.kind === 'image') {
    await removeFile(next.posterPath);
    next.poster = '';
    next.posterPath = '';
  }

  await updateDoc(doc(db, 'media', item.id), next);
}

export async function deleteMedia(item: MediaRecord) {
  await Promise.all([removeFile(item.storagePath), removeFile(item.posterPath)]);
  await deleteDoc(doc(db, 'media', item.id));
}
